import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from '../theme';

// design-system unit (2026-06-10、Option C):
// - localStorage キー: conftrack:color-mode (light/dark)
// - 初回未トグル時は prefers-color-scheme に追従
// - Toggle で localStorage 保存 + state 更新 + theme 再生成
// - FOUC 対策: SPA 性質上、初回 1 frame のチラつきは許容

const STORAGE_KEY = 'conftrack:color-mode';

interface ColorModeContextValue {
  mode: PaletteMode;
  toggleMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

export const useColorMode = (): ColorModeContextValue => {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used within ColorModeProvider');
  }
  return ctx;
};

// 初期 mode を決定: localStorage > prefers-color-scheme > 'light'
const getInitialMode = (): PaletteMode => {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage が使えない (シークレットモード等) は無視
  }
  if (typeof window.matchMedia === 'function') {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    if (mql.matches) return 'dark';
  }
  return 'light';
};

interface ColorModeProviderProps {
  children: ReactNode;
}

export const ColorModeProvider: React.FC<ColorModeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(getInitialMode);

  // mode 変更時に localStorage 保存
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // 書き込み失敗は無視 (in-memory only)
    }
  }, [mode]);

  const toggleMode = (): void => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggleMode }), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
